import { useState } from 'react';
import { useRevalidator } from 'react-router';
import { toast } from 'sonner';
import { projectNameSchema } from '@shared/schemas/project';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Database } from '@/types/database.types';
import { getAuthHeaders } from '@/utils/auth';

type Project = Database['public']['Tables']['projects']['Row'];
type ProjectTeam = Database['public']['Tables']['projects_team']['Row'];
type Member = Database['public']['Tables']['project_members']['Row'];
type ProjectWithTeamAndMembers = Project & {
  projects_team: ProjectTeam;
} & { project_members: Member[] | null };

export default function RenameProjectForm({
  project,
}: {
  project: ProjectWithTeamAndMembers;
}) {
  const revalidator = useRevalidator();
  const [name, setName] = useState(project.project_name);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const parseResult = projectNameSchema.safeParse(name);
  const validationError =
    !parseResult.success && name !== project.project_name
      ? parseResult.error.issues[0].message
      : null;
  const isUnchanged = parseResult.success
    ? parseResult.data === project.project_name
    : true;
  const canSave = parseResult.success && !isUnchanged && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setSubmitError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `/api/engine/projects/${project.id}/update-name`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ newName: name }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        toast.success('Project name updated');
        revalidator.revalidate();
        return;
      }

      if (data.phase === 'external-update') {
        setSubmitError('Review service update failed. Please try again.');
      } else if (data.phase === 'database-update') {
        setSubmitError('Database save failed. Please try again.');
      } else {
        setSubmitError(data.error ?? 'Something went wrong. Please try again.');
      }
    } catch (_error) {
      setSubmitError('Network error. Please check your connection.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border p-6 w-full max-w-3xl">
      <div className="flex items-center gap-4">
        <Label
          htmlFor="project-name"
          className="text-lg font-semibold whitespace-nowrap"
        >
          Project name
        </Label>
        <Input
          id="project-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSaving}
          autoComplete="off"
          className="flex-1 min-w-0"
        />
        <Button
          className="cursor-pointer"
          disabled={!canSave}
          onClick={handleSave}
        >
          {isSaving ? 'Saving...' : 'Update'}
        </Button>
      </div>
      {(submitError || validationError) && (
        <p className="text-sm text-destructive" role="alert">
          {submitError ?? validationError}
        </p>
      )}
    </div>
  );
}
